import React, { useState, useEffect } from 'react';
import {
    Box,
    Heading,
    Text,
    Container,
    VStack,
    SimpleGrid,
    Card,
    CardBody,
    CardHeader,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Button,
    HStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    useColorMode,
    Spinner,
    Alert,
    AlertIcon,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    FormControl,
    FormLabel,
    Input,
    Select,
    IconButton,
    Tooltip,
    useToast,
    Flex,
    Divider,
    Icon,
} from '@chakra-ui/react';
import { FaPlus, FaCopy, FaFileExport, FaDownload } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useBalanceService } from '@/hooks/useBalanceService';
import { useAuth } from '@/contexts/AuthContext';

// Extend window object to include right sidebar update function (type aligned with RightSidebar)
declare global {
    interface Window {
        updateRightSidebar?: (actions: Array<{
            label: string;
            icon?: React.ComponentType;
            colorScheme: string;
            variant?: string;
            onClick?: () => void;
        }>) => void;
    }
}

interface Transaction {
    id?: string;
    type?: string;
    amount?: any;
    status?: string;
    created_at?: string;
    balance_before?: any;
    balance_after?: any;
}

interface CryptoAddress {
    id?: string;
    currency?: string;
    address?: string;
    memo?: string;
    network?: string;
    label?: string;
    is_default?: boolean;
    created_at?: string;
}

interface FiatDetails {
    id?: string;
    currency?: string;
    beneficiary_name?: string;
    beneficiary_address?: string;
    bank_details?: any;
    payment_system?: string;
    country?: string;
    label?: string;
    is_default?: boolean;
    created_at?: string;
}

interface Currency {
    code?: string;
    name?: string;
    precision?: number;
    deposit_enabled?: boolean;
    withdrawal_enabled?: boolean;
    network?: string;
}

interface Amount {
    units?: number;
    nanos?: number;
    currency?: string;
}

interface Balance {
    currency?: string;
    available?: Amount;
    locked?: Amount;
}

export const BalancePage: React.FC = () => {
    const { t } = useTranslation();
    const { colorSchemes } = useThemeContext();
    const toast = useToast();
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const balanceService = useBalanceService();

    // Font sizes
    const largeFontSize = "lg";
    const smallFontSize = "sm";

    // Spacings
    const mainSpacing = 8;
    const sectionSpacing = 6;
    const itemSpacing = 4;
    const smallSpacing = 2;

    // Sizes
    const iconSize = 4;
    const headerBottomMargin = 4;

    // Toast durations
    const successDuration = 3000;
    const errorDuration = 5000;
    const infoDuration = 3000;
    const shortDuration = 2000;

    const [balances, setBalances] = useState<Balance[]>([]);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [cryptoAddresses, setCryptoAddresses] = useState<CryptoAddress[]>([]);
    const [fiatDetails, setFiatDetails] = useState<FiatDetails[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>('');

    // Modal states
    const depositModal = useDisclosure();
    const withdrawalModal = useDisclosure();
    const cryptoAddressModal = useDisclosure();
    const fiatDetailsModal = useDisclosure();
    const createBalancesModal = useDisclosure();

    // Form states
    const [depositForm, setDepositForm] = useState({
        amount: '',
        currency: 'USD',
    });

    const [withdrawalForm, setWithdrawalForm] = useState({
        amount: '',
        currency: 'USD',
        type: 'crypto' as 'crypto' | 'fiat', // crypto or fiat
        selectedAddressId: '',
        selectedFiatDetailsId: '',
        address: '',
        memo: '',
    });

    const [cryptoAddressForm, setCryptoAddressForm] = useState({
        currency: '',
        address: '',
        memo: '',
        network: '',
        label: '',
        isDefault: false,
    });

    const [fiatDetailsForm, setFiatDetailsForm] = useState({
        currency: '',
        beneficiaryName: '',
        beneficiaryAddress: '',
        bankDetails: {
            accountNumber: '',
            bankName: '',
            swiftCode: '',
            iban: '',
        },
        paymentSystem: '',
        country: '',
        label: '',
        isDefault: false,
    });

    const [createBalancesForm, setCreateBalancesForm] = useState({
        selectedCurrencies: [] as string[],
    });

    // Tab state
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    // Function to update right sidebar based on active tab
    const updateRightSidebarForTab = (tabIndex: number) => {
        if (!window.updateRightSidebar) return;

        // Icon components (defined here to access theme variables)
        const AddIcon = () => (
            <Icon viewBox="0 0 24 24" boxSize={iconSize} color="currentColor">
                <path
                    fill="currentColor"
                    d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
                />
            </Icon>
        );

        const ExportIcon = () => (
            <Icon viewBox="0 0 24 24" boxSize={iconSize} color="currentColor">
                <path
                    fill="currentColor"
                    d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                />
            </Icon>
        );

        switch (tabIndex) {
            case 0: // Balance Overview
                window.updateRightSidebar([
                    {
                        label: 'Create Balances',
                        icon: AddIcon,
                        colorScheme: colorSchemes.primary,
                        onClick: createBalancesModal.onOpen,
                    },
                ]);
                break;
            case 1: // Deposit & Withdrawal
                window.updateRightSidebar([
                    {
                        label: 'Create Deposit',
                        icon: AddIcon,
                        colorScheme: colorSchemes.success,
                        onClick: depositModal.onOpen,
                    },
                    {
                        label: 'Create Withdrawal',
                        icon: AddIcon,
                        colorScheme: colorSchemes.error,
                        onClick: withdrawalModal.onOpen,
                    },
                ]);
                break;
            case 2: // Crypto Addresses
                window.updateRightSidebar([
                    {
                        label: 'Add Address',
                        icon: AddIcon,
                        colorScheme: colorSchemes.primary,
                        onClick: cryptoAddressModal.onOpen,
                    },
                ]);
                break;
            case 3: // Bank Details
                window.updateRightSidebar([
                    {
                        label: 'Add Bank Details',
                        icon: AddIcon,
                        colorScheme: colorSchemes.primary,
                        onClick: fiatDetailsModal.onOpen,
                    },
                ]);
                break;
            case 4: // Transaction History
                window.updateRightSidebar([
                    {
                        label: 'Export Transactions',
                        icon: ExportIcon,
                        colorScheme: colorSchemes.neutral,
                        onClick: () => {
                            toast({
                                title: 'Export Feature',
                                description: 'Export functionality will be implemented soon.',
                                status: 'info',
                                duration: infoDuration,
                                isClosable: true,
                            });
                        },
                    },
                ]);
                break;
            default:
                window.updateRightSidebar([]);
        }
    };

    // Handle tab change
    const handleTabChange = (index: number) => {
        setActiveTabIndex(index);
        updateRightSidebarForTab(index);

        // Load tab-specific data when switching to a new tab
        loadTabData(index);
    };

    useEffect(() => {
        const initializePage = async () => {
            // Wait for auth context to finish loading
            if (authLoading) {
                console.log('Auth context still loading, waiting...');
                return;
            }

            // Check if user is authenticated
            if (!isAuthenticated) {
                console.log('User not authenticated, redirecting to login');
                window.location.href = '/login';
                return;
            }

            console.log('User is authenticated, loading page data...');

            // Load page data - AuthContext will handle token refresh automatically if needed
            await loadAllData();
        };

        initializePage();
    }, [authLoading, isAuthenticated]); // Added authLoading and isAuthenticated as dependencies

    // Update right sidebar when component mounts or tab changes
    useEffect(() => {
        updateRightSidebarForTab(activeTabIndex);

        // Load data for the initial tab
        if (currencies.length > 0) {
            loadTabData(activeTabIndex);
        }

        // Clean up when component unmounts
        return () => {
            if (window.updateRightSidebar) {
                window.updateRightSidebar([]);
            }
        };
    }, [activeTabIndex, currencies.length]);

    const loadAllData = async () => {
        try {
            setIsLoading(true);
            setError('');

            // Load basic data that's always needed
            console.log('Loading basic balance data...');

            // Load balances first
            const balanceData = await balanceService.getBalances();
            setBalances(balanceData);
            console.log('Balances loaded:', balanceData.length);

            // Load currencies (needed for forms)
            const currencyData = await balanceService.listCurrencies({
                onlyEnabled: true,
            });
            setCurrencies((currencyData.currencies || []) as Currency[]);
            console.log('Currencies loaded:', (currencyData.currencies || []).length);

        } catch (err: any) {
            console.error('Error loading basic balance data:', err);

            // Don't show error if user was redirected to login
            if (err.message && !err.message.includes('redirect') && !err.message.includes('login')) {
                setError(err.message || 'Failed to load balance data');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Load tab-specific data
    const loadTabData = async (tabIndex: number) => {
        try {
            switch (tabIndex) {
                case 1: // Deposit & Withdrawal - load transactions
                    if (transactions.length === 0) {
                        console.log('Loading transactions...');
                        const transactionData = await balanceService.getTransactions({
                            limit: 50,
                            offset: 0,
                        });
                        setTransactions((transactionData.transactions || []) as Transaction[]);
                        console.log('Transactions loaded:', (transactionData.transactions || []).length);
                    }
                    break;

                case 2: // Crypto Addresses
                    if (cryptoAddresses.length === 0) {
                        console.log('Loading crypto addresses...');
                        try {
                            const cryptoData = await balanceService.listCryptoAddresses();
                            setCryptoAddresses((cryptoData.addresses || []) as CryptoAddress[]);
                            console.log('Crypto addresses loaded:', (cryptoData.addresses || []).length);
                        } catch (err: any) {
                            console.warn('Failed to load crypto addresses:', err.message);
                        }
                    }
                    break;

                case 3: // Bank Details
                    if (fiatDetails.length === 0) {
                        console.log('Loading fiat details...');
                        try {
                            const fiatData = await balanceService.listFiatDetails();
                            setFiatDetails((fiatData.details || []) as FiatDetails[]);
                            console.log('Fiat details loaded:', (fiatData.details || []).length);
                        } catch (err: any) {
                            console.warn('Failed to load fiat details:', err.message);
                        }
                    }
                    break;

                case 4: // Transaction History
                    if (transactions.length === 0) {
                        console.log('Loading full transaction history...');
                        const transactionData = await balanceService.getTransactions({
                            limit: 100,
                            offset: 0,
                        });
                        setTransactions((transactionData.transactions || []) as Transaction[]);
                        console.log('Transaction history loaded:', (transactionData.transactions || []).length);
                    }
                    break;

                default:
                    // Tab 0 (Balance Overview) doesn't need additional data
                    break;
            }
        } catch (err: any) {
            console.error(`Error loading data for tab ${tabIndex}:`, err);
            toast({
                title: 'Failed to Load Data',
                description: err.message || 'Failed to load tab data',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const formatAmount = (amount: any) => {
        if (!amount) return '0.00';
        const value = (amount.units || 0) + (amount.nanos || 0) / 1000000000;
        return value.toFixed(2);
    };

    const handleDeposit = async () => {
        try {
            const value = parseFloat(depositForm.amount);
            const units = Math.floor(value);
            const nanos = Math.round((value - units) * 1e9);
            await balanceService.createDeposit({
                amount: {
                    units,
                    nanos,
                    currency: depositForm.currency,
                },
                currency: depositForm.currency,
            });

            toast({
                title: 'Deposit Created',
                description: 'Your deposit has been initiated successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            depositModal.onClose();
            setDepositForm({ amount: '', currency: 'USD' });

            // Reload balances and transactions
            const balanceData = await balanceService.getBalances();
            setBalances(balanceData);

            const transactionData = await balanceService.getTransactions({
                limit: 50,
                offset: 0,
            });
            setTransactions((transactionData.transactions || []) as Transaction[]);
        } catch (err: any) {
            console.error('Failed to create deposit:', err);
            toast({
                title: 'Deposit Failed',
                description: err.message || 'Failed to create deposit',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleWithdrawal = async () => {
        try {
            let addressToUse = '';
            let memoToUse = '';

            if (withdrawalForm.type === 'crypto') {
                if (withdrawalForm.selectedAddressId) {
                    // Use selected crypto address
                    const selectedAddress = cryptoAddresses.find(addr => addr.id === withdrawalForm.selectedAddressId);
                    if (selectedAddress) {
                        addressToUse = selectedAddress.address || '';
                        memoToUse = selectedAddress.memo || '';
                    }
                } else {
                    // Use manually entered address
                    addressToUse = withdrawalForm.address;
                    memoToUse = withdrawalForm.memo;
                }
            } else {
                // For fiat withdrawal, use bank details ID as "address"
                if (withdrawalForm.selectedFiatDetailsId) {
                    addressToUse = withdrawalForm.selectedFiatDetailsId;
                } else {
                    throw new Error('Please select bank details for fiat withdrawal');
                }
            }

            await balanceService.createWithdrawal({
                amount: withdrawalForm.amount,
                currency: withdrawalForm.currency,
                address: addressToUse,
                memo: memoToUse,
            });

            toast({
                title: 'Withdrawal Created',
                description: 'Your withdrawal has been initiated successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            withdrawalModal.onClose();
            setWithdrawalForm({ amount: '', currency: 'USD', address: '', memo: '', type: 'crypto', selectedAddressId: '', selectedFiatDetailsId: '' });

            // Reload balances and transactions
            const balanceData = await balanceService.getBalances();
            setBalances(balanceData);

            const transactionData = await balanceService.getTransactions({
                limit: 50,
                offset: 0,
            });
            setTransactions((transactionData.transactions || []) as Transaction[]);
        } catch (err: any) {
            console.error('Failed to create withdrawal:', err);
            toast({
                title: 'Withdrawal Failed',
                description: err.message || 'Failed to create withdrawal',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleAddCryptoAddress = async () => {
        try {
            await balanceService.createCryptoAddress(cryptoAddressForm);

            toast({
                title: 'Address Added',
                description: 'Crypto address has been added successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            cryptoAddressModal.onClose();
            setCryptoAddressForm({
                currency: '',
                address: '',
                memo: '',
                network: '',
                label: '',
                isDefault: false,
            });

            // Reload crypto addresses specifically
            try {
                const cryptoData = await balanceService.listCryptoAddresses();
                setCryptoAddresses((cryptoData.addresses || []) as CryptoAddress[]);
            } catch (err: any) {
                console.warn('Failed to reload crypto addresses:', err.message);
            }
        } catch (err: any) {
            console.error('Failed to add crypto address:', err);
            toast({
                title: 'Failed to Add Address',
                description: err.message || 'Failed to add crypto address',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleAddFiatDetails = async () => {
        try {
            // Convert only bankDetails to snake_case, other fields stay in camelCase
            const dataToSend = {
                ...fiatDetailsForm,
                bankDetails: {
                    account_number: fiatDetailsForm.bankDetails.accountNumber,
                    bank_name: fiatDetailsForm.bankDetails.bankName,
                    swift_code: fiatDetailsForm.bankDetails.swiftCode,
                    iban: fiatDetailsForm.bankDetails.iban,
                },
            };

            console.log('Sending fiat details data:', dataToSend);

            await balanceService.createFiatDetails(dataToSend);

            toast({
                title: 'Bank Details Added',
                description: 'Bank details have been added successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            fiatDetailsModal.onClose();
            setFiatDetailsForm({
                currency: '',
                beneficiaryName: '',
                beneficiaryAddress: '',
                bankDetails: {
                    accountNumber: '',
                    bankName: '',
                    swiftCode: '',
                    iban: '',
                },
                paymentSystem: '',
                country: '',
                label: '',
                isDefault: false,
            });

            // Reload fiat details specifically
            try {
                const fiatData = await balanceService.listFiatDetails();
                setFiatDetails((fiatData.details || []) as FiatDetails[]);
            } catch (err: any) {
                console.warn('Failed to reload fiat details:', err.message);
            }
        } catch (err: any) {
            console.error('Failed to add fiat details:', err);
            toast({
                title: 'Failed to Add Bank Details',
                description: err.message || 'Failed to add bank details',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleCreateBalances = async () => {
        try {
            if (createBalancesForm.selectedCurrencies.length === 0) {
                toast({
                    title: 'No Currencies Selected',
                    description: 'Please select at least one currency to create balances.',
                    status: 'warning',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            await balanceService.ensureBalancesForUser(
                undefined, // userId будет взят из контекста на бэкенде
                createBalancesForm.selectedCurrencies
            );

            toast({
                title: 'Balances Created',
                description: `Created balances for ${createBalancesForm.selectedCurrencies.join(', ')} currencies.`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            createBalancesModal.onClose();
            setCreateBalancesForm({ selectedCurrencies: [] });

            // Reload balances specifically
            const balanceData = await balanceService.getBalances();
            setBalances(balanceData);
        } catch (err: any) {
            console.error('Failed to create balances:', err);
            toast({
                title: 'Failed to Create Balances',
                description: err.message || 'Failed to create balances',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied to Clipboard',
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    const getStatusBadge = (status: string) => {
        const colorSchemeMap: Record<string, string> = {
            completed: colorSchemes.success,
            pending: colorSchemes.warning,
            failed: colorSchemes.error,
            cancelled: colorSchemes.neutral,
        };

        return <Badge colorScheme={colorSchemeMap[status] || colorSchemes.neutral}>{status}</Badge>;
    };

    const getTransactionTypeBadge = (type: string) => {
        const colorSchemeMap: Record<string, string> = {
            deposit: colorSchemes.success,
            withdrawal: colorSchemes.error,
            transfer: colorSchemes.primary,
            adjustment: colorSchemes.info,
        };

        return <Badge colorScheme={colorSchemeMap[type] || colorSchemes.neutral}>{type}</Badge>;
    };

    // Show loading spinner while auth is loading or page data is loading
    if (authLoading || isLoading) {
        return (
            <Container maxW="7xl" py={mainSpacing}>
                <VStack spacing={mainSpacing}>
                    <Spinner size="xl" />
                    <Text>{authLoading ? 'Authenticating...' : t('common.loading')}</Text>
                </VStack>
            </Container>
        );
    }

    // Show authentication error if not authenticated and not loading
    if (!authLoading && !isAuthenticated) {
        return (
            <Container maxW="7xl" py={mainSpacing}>
                <Alert status="warning">
                    <AlertIcon />
                    <VStack align="start" spacing={2}>
                        <Text>Authentication required to access this page.</Text>
                        <Button colorScheme={colorSchemes.primary} size="sm" onClick={() => window.location.href = '/login'}>
                            Go to Login
                        </Button>
                    </VStack>
                </Alert>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxW="7xl" py={mainSpacing}>
                <Alert status="error">
                    <AlertIcon />
                    {error}
                </Alert>
            </Container>
        );
    }

    // Calculate totals
    const totalAvailable = balances.reduce((sum, balance) => {
        const value = parseFloat(formatAmount(balance.available));
        return sum + value;
    }, 0);

    const totalLocked = balances.reduce((sum, balance) => {
        const value = parseFloat(formatAmount(balance.locked));
        return sum + value;
    }, 0);

    const totalBalance = totalAvailable + totalLocked;

    return (
        <Container maxW="7xl" py={mainSpacing}>
            <VStack spacing={mainSpacing} align="stretch">
                {/* Header */}
                <Box>
                    <Heading size="xl" mb={headerBottomMargin}>{t('balance.title')}</Heading>
                    <Text fontSize={largeFontSize} color="text.secondary">
                        {t('balance.manageBalance')}
                    </Text>
                </Box>

                {/* Balance Summary Cards */}
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={sectionSpacing}>
                    <Card bg="bg.surface">
                        <CardBody>
                            <Stat>
                                <StatLabel>{t('balance.totalBalance')}</StatLabel>
                                <StatNumber>${totalBalance.toFixed(2)}</StatNumber>
                                <StatHelpText>{t('balance.totalFunds')}</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>

                    <Card bg="bg.surface">
                        <CardBody>
                            <Stat>
                                <StatLabel>{t('balance.availableBalance')}</StatLabel>
                                <StatNumber>${totalAvailable.toFixed(2)}</StatNumber>
                                <StatHelpText>{t('balance.availableForWithdrawal')}</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>

                    <Card bg="bg.surface">
                        <CardBody>
                            <Stat>
                                <StatLabel>{t('balance.frozenBalance')}</StatLabel>
                                <StatNumber>${totalLocked.toFixed(2)}</StatNumber>
                                <StatHelpText>{t('balance.lockedTransactions')}</StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>
                </SimpleGrid>

                {/* Main Content Tabs */}
                <Tabs colorScheme={colorSchemes.primary} index={activeTabIndex} onChange={handleTabChange}>
                    <TabList>
                        <Tab>Balance Overview</Tab>
                        <Tab>Deposit & Withdrawal</Tab>
                        <Tab>Crypto Addresses</Tab>
                        <Tab>Bank Details</Tab>
                        <Tab>Transaction History</Tab>
                    </TabList>

                    <TabPanels>
                        {/* Balance Overview Tab */}
                        <TabPanel>
                            <Card bg="bg.surface">
                                <CardHeader>
                                    <Heading size="md">Currency Balances</Heading>
                                </CardHeader>
                                <CardBody>
                                    <Table variant="simple">
                                        <Thead>
                                            <Tr>
                                                <Th>Currency</Th>
                                                <Th isNumeric>Available</Th>
                                                <Th isNumeric>Locked</Th>
                                                <Th isNumeric>Total</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {balances.map((balance, index) => (
                                                <Tr key={index}>
                                                    <Td>{balance.currency}</Td>
                                                    <Td isNumeric>{formatAmount(balance.available)}</Td>
                                                    <Td isNumeric>{formatAmount(balance.locked)}</Td>
                                                    <Td isNumeric>
                                                        {(parseFloat(formatAmount(balance.available)) +
                                                            parseFloat(formatAmount(balance.locked))).toFixed(2)}
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        </TabPanel>

                        {/* Deposit & Withdrawal Tab */}
                        <TabPanel>
                            <VStack spacing={sectionSpacing} align="stretch">
                                <Text fontSize={largeFontSize} fontWeight="semibold">Recent Deposits & Withdrawals</Text>
                                <Table variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th>Type</Th>
                                            <Th>Amount</Th>
                                            <Th>Status</Th>
                                            <Th>Date</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {transactions
                                            .filter(tx => tx.type === 'deposit' || tx.type === 'withdrawal')
                                            .slice(0, 10)
                                            .map((transaction) => (
                                                <Tr key={transaction.id}>
                                                    <Td>{getTransactionTypeBadge(transaction.type || 'unknown')}</Td>
                                                    <Td>{formatAmount(transaction.amount)} {transaction.amount?.currency}</Td>
                                                    <Td>{getStatusBadge(transaction.status || 'unknown')}</Td>
                                                    <Td>{transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : 'N/A'}</Td>
                                                </Tr>
                                            ))}
                                    </Tbody>
                                </Table>
                            </VStack>
                        </TabPanel>

                        {/* Crypto Addresses Tab */}
                        <TabPanel>
                            <VStack spacing={sectionSpacing} align="stretch">
                                <Text fontSize={largeFontSize} fontWeight="semibold">Cryptocurrency Addresses</Text>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={itemSpacing}>
                                    {cryptoAddresses.map((address) => (
                                        <Card key={address.id} bg="bg.surface">
                                            <CardBody>
                                                <VStack align="start" spacing={2}>
                                                    <HStack justify="space-between" w="full">
                                                        <Badge colorScheme={colorSchemes.primary}>{address.currency}</Badge>
                                                        {address.is_default && <Badge colorScheme={colorSchemes.success}>Default</Badge>}
                                                    </HStack>

                                                    <Text fontWeight="semibold">{address.label || 'Unnamed Address'}</Text>

                                                    <HStack>
                                                        <Text fontSize={smallFontSize} color="text.muted" isTruncated maxW="300px">
                                                            {address.address}
                                                        </Text>
                                                        <IconButton
                                                            size="sm"
                                                            icon={<Icon as={FaCopy} />}
                                                            aria-label="Copy address"
                                                            onClick={() => copyToClipboard(address.address || '')}
                                                        />
                                                    </HStack>

                                                    {address.memo && (
                                                        <HStack>
                                                            <Text fontSize={smallFontSize} color="text.muted">Memo: {address.memo}</Text>
                                                            <IconButton
                                                                size="sm"
                                                                icon={<Icon as={FaCopy} />}
                                                                aria-label="Copy memo"
                                                                onClick={() => copyToClipboard(address.memo!)}
                                                            />
                                                        </HStack>
                                                    )}

                                                    {address.network && (
                                                        <Text fontSize={smallFontSize} color="text.muted">Network: {address.network}</Text>
                                                    )}
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    ))}
                                </SimpleGrid>
                            </VStack>
                        </TabPanel>

                        {/* Bank Details Tab */}
                        <TabPanel>
                            <VStack spacing={sectionSpacing} align="stretch">
                                <Text fontSize={largeFontSize} fontWeight="semibold">Bank Account Details</Text>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={itemSpacing}>
                                    {fiatDetails.map((details) => (
                                        <Card key={details.id} bg="bg.surface">
                                            <CardBody>
                                                <VStack align="start" spacing={2}>
                                                    <HStack justify="space-between" w="full">
                                                        <Badge colorScheme={colorSchemes.info}>{details.currency}</Badge>
                                                        {details.is_default && <Badge colorScheme={colorSchemes.success}>Default</Badge>}
                                                    </HStack>

                                                    <Text fontWeight="semibold">{details.label || 'Unnamed Bank Account'}</Text>
                                                    <Text>{details.beneficiary_name}</Text>

                                                    <Divider />

                                                    <Text fontSize={smallFontSize}><strong>Bank:</strong> {details.bank_details?.bank_name}</Text>
                                                    <Text fontSize={smallFontSize}><strong>Account:</strong> {details.bank_details?.account_number}</Text>
                                                    {details.bank_details?.swift_code && (
                                                        <Text fontSize={smallFontSize}><strong>SWIFT:</strong> {details.bank_details.swift_code}</Text>
                                                    )}
                                                    {details.bank_details?.iban && (
                                                        <Text fontSize={smallFontSize}><strong>IBAN:</strong> {details.bank_details.iban}</Text>
                                                    )}
                                                    {details.country && (
                                                        <Text fontSize={smallFontSize}><strong>Country:</strong> {details.country}</Text>
                                                    )}
                                                </VStack>
                                            </CardBody>
                                        </Card>
                                    ))}
                                </SimpleGrid>
                            </VStack>
                        </TabPanel>

                        {/* Transaction History Tab */}
                        <TabPanel>
                            <Card bg="bg.surface">
                                <CardHeader>
                                    <Heading size="md">Transaction History</Heading>
                                </CardHeader>
                                <CardBody>
                                    {transactions.length === 0 ? (
                                        <VStack spacing={itemSpacing} py={mainSpacing}>
                                            <Text color="text.muted" fontSize={largeFontSize}>No transactions found</Text>
                                            <Text color="text.muted" fontSize={smallFontSize}>
                                                Create your first deposit or withdrawal to see transactions here.
                                            </Text>
                                        </VStack>
                                    ) : (
                                        <Table variant="simple">
                                            <Thead>
                                                <Tr>
                                                    <Th>ID</Th>
                                                    <Th>Type</Th>
                                                    <Th>Amount</Th>
                                                    <Th>Status</Th>
                                                    <Th>Date</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {transactions.map((transaction) => (
                                                    <Tr key={transaction.id || Math.random()}>
                                                        <Td>
                                                            <Text fontSize={smallFontSize} fontFamily="mono">
                                                                {transaction.id ? transaction.id.substring(0, 8) + '...' : 'N/A'}
                                                            </Text>
                                                        </Td>
                                                        <Td>{getTransactionTypeBadge(transaction.type || 'unknown')}</Td>
                                                        <Td>{formatAmount(transaction.amount)} {transaction.amount?.currency}</Td>
                                                        <Td>{getStatusBadge(transaction.status || 'unknown')}</Td>
                                                        <Td>{transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : 'N/A'}</Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    )}
                                </CardBody>
                            </Card>
                        </TabPanel>
                    </TabPanels>
                </Tabs>

                {/* Deposit Modal */}
                <Modal isOpen={depositModal.isOpen} onClose={depositModal.onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Create Deposit</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={6}>
                            <VStack spacing={4}>
                                <FormControl>
                                    <FormLabel>Currency</FormLabel>
                                    <Select
                                        value={depositForm.currency}
                                        onChange={(e) => setDepositForm({ ...depositForm, currency: e.target.value })}
                                    >
                                        {currencies
                                            .filter(c => c.deposit_enabled)
                                            .map((currency) => (
                                                <option key={currency.code} value={currency.code}>
                                                    {currency.name} ({currency.code})
                                                </option>
                                            ))}
                                    </Select>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Amount</FormLabel>
                                    <Input
                                        type="number"
                                        placeholder="0.00"
                                        value={depositForm.amount}
                                        onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
                                    />
                                </FormControl>

                                <Button colorScheme={colorSchemes.success} onClick={handleDeposit} w="full">
                                    Create Deposit
                                </Button>
                            </VStack>
                        </ModalBody>
                    </ModalContent>
                </Modal>

                {/* Withdrawal Modal */}
                <Modal isOpen={withdrawalModal.isOpen} onClose={withdrawalModal.onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Create Withdrawal</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={sectionSpacing}>
                            <VStack spacing={itemSpacing}>
                                <FormControl>
                                    <FormLabel>Currency</FormLabel>
                                    <Select
                                        value={withdrawalForm.currency}
                                        onChange={(e) => setWithdrawalForm({ ...withdrawalForm, currency: e.target.value })}
                                    >
                                        {currencies
                                            .filter(c => c.withdrawal_enabled)
                                            .map((currency) => (
                                                <option key={currency.code} value={currency.code}>
                                                    {currency.name} ({currency.code})
                                                </option>
                                            ))}
                                    </Select>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Amount</FormLabel>
                                    <Input
                                        type="number"
                                        placeholder="0.00"
                                        value={withdrawalForm.amount}
                                        onChange={(e) => setWithdrawalForm({ ...withdrawalForm, amount: e.target.value })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Type</FormLabel>
                                    <Select
                                        value={withdrawalForm.type}
                                        onChange={(e) => setWithdrawalForm({ ...withdrawalForm, type: e.target.value as 'crypto' | 'fiat' })}
                                    >
                                        <option value="crypto">Crypto</option>
                                        <option value="fiat">Fiat</option>
                                    </Select>
                                </FormControl>

                                {withdrawalForm.type === 'crypto' ? (
                                    <>
                                        <FormControl>
                                            <FormLabel>Select Crypto Address</FormLabel>
                                            <Select
                                                value={withdrawalForm.selectedAddressId}
                                                onChange={(e) => setWithdrawalForm({ ...withdrawalForm, selectedAddressId: e.target.value })}
                                            >
                                                <option value="">Select existing address or enter manually</option>
                                                {cryptoAddresses
                                                    .filter(addr => addr.currency === withdrawalForm.currency)
                                                    .map((address) => (
                                                        <option key={address.id} value={address.id}>
                                                            {address.label || 'Unnamed'} - {address.address?.substring(0, 20)}...
                                                        </option>
                                                    ))}
                                            </Select>
                                        </FormControl>

                                        {!withdrawalForm.selectedAddressId && (
                                            <>
                                                <FormControl>
                                                    <FormLabel>Address</FormLabel>
                                                    <HStack>
                                                        <Input
                                                            placeholder="Enter withdrawal address"
                                                            value={withdrawalForm.address}
                                                            onChange={(e) => setWithdrawalForm({ ...withdrawalForm, address: e.target.value })}
                                                        />
                                                        <Button
                                                            colorScheme={colorSchemes.primary}
                                                            size="sm"
                                                            onClick={() => {
                                                                // Pre-fill currency in crypto address form
                                                                setCryptoAddressForm({
                                                                    ...cryptoAddressForm,
                                                                    currency: withdrawalForm.currency
                                                                });
                                                                withdrawalModal.onClose();
                                                                cryptoAddressModal.onOpen();
                                                            }}
                                                        >
                                                            Add New
                                                        </Button>
                                                    </HStack>
                                                </FormControl>

                                                <FormControl>
                                                    <FormLabel>Memo (Optional)</FormLabel>
                                                    <Input
                                                        placeholder="Enter memo if required"
                                                        value={withdrawalForm.memo}
                                                        onChange={(e) => setWithdrawalForm({ ...withdrawalForm, memo: e.target.value })}
                                                    />
                                                </FormControl>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    // Fiat withdrawal
                                    <>
                                        <FormControl>
                                            <FormLabel>Select Bank Details</FormLabel>
                                            <Select
                                                value={withdrawalForm.selectedFiatDetailsId}
                                                onChange={(e) => setWithdrawalForm({ ...withdrawalForm, selectedFiatDetailsId: e.target.value })}
                                            >
                                                <option value="">Select bank details</option>
                                                {fiatDetails
                                                    .filter(details => details.currency === withdrawalForm.currency)
                                                    .map((details) => (
                                                        <option key={details.id} value={details.id}>
                                                            {details.label || 'Unnamed'} - {details.bank_details?.bank_name}
                                                        </option>
                                                    ))}
                                            </Select>
                                        </FormControl>

                                        {!withdrawalForm.selectedFiatDetailsId && (
                                            <Button
                                                colorScheme={colorSchemes.primary}
                                                variant="outline"
                                                onClick={() => {
                                                    // Pre-fill currency in fiat details form
                                                    setFiatDetailsForm({
                                                        ...fiatDetailsForm,
                                                        currency: withdrawalForm.currency
                                                    });
                                                    withdrawalModal.onClose();
                                                    fiatDetailsModal.onOpen();
                                                }}
                                                w="full"
                                            >
                                                Add New Bank Details
                                            </Button>
                                        )}
                                    </>
                                )}

                                <Button colorScheme={colorSchemes.error} onClick={handleWithdrawal} w="full">
                                    Create Withdrawal
                                </Button>
                            </VStack>
                        </ModalBody>
                    </ModalContent>
                </Modal>

                {/* Crypto Address Modal */}
                <Modal isOpen={cryptoAddressModal.isOpen} onClose={cryptoAddressModal.onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Add Crypto Address</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={sectionSpacing}>
                            <VStack spacing={itemSpacing}>
                                <FormControl>
                                    <FormLabel>Currency</FormLabel>
                                    <Select
                                        value={cryptoAddressForm.currency}
                                        onChange={(e) => setCryptoAddressForm({ ...cryptoAddressForm, currency: e.target.value })}
                                    >
                                        <option value="">Select Currency</option>
                                        {currencies.map((currency) => (
                                            <option key={currency.code} value={currency.code}>
                                                {currency.name} ({currency.code})
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Address</FormLabel>
                                    <Input
                                        placeholder="Enter crypto address"
                                        value={cryptoAddressForm.address}
                                        onChange={(e) => setCryptoAddressForm({ ...cryptoAddressForm, address: e.target.value })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Label</FormLabel>
                                    <Input
                                        placeholder="My Wallet"
                                        value={cryptoAddressForm.label}
                                        onChange={(e) => setCryptoAddressForm({ ...cryptoAddressForm, label: e.target.value })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Network (Optional)</FormLabel>
                                    <Input
                                        placeholder="e.g., ERC-20, TRC-20"
                                        value={cryptoAddressForm.network}
                                        onChange={(e) => setCryptoAddressForm({ ...cryptoAddressForm, network: e.target.value })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Memo (Optional)</FormLabel>
                                    <Input
                                        placeholder="Enter memo if required"
                                        value={cryptoAddressForm.memo}
                                        onChange={(e) => setCryptoAddressForm({ ...cryptoAddressForm, memo: e.target.value })}
                                    />
                                </FormControl>

                                <Button colorScheme={colorSchemes.primary} onClick={handleAddCryptoAddress} w="full">
                                    Add Address
                                </Button>
                            </VStack>
                        </ModalBody>
                    </ModalContent>
                </Modal>

                {/* Fiat Details Modal */}
                <Modal isOpen={fiatDetailsModal.isOpen} onClose={fiatDetailsModal.onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Add Bank Details</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={sectionSpacing}>
                            <VStack spacing={itemSpacing}>
                                <FormControl>
                                    <FormLabel>Currency</FormLabel>
                                    <Select
                                        value={fiatDetailsForm.currency}
                                        onChange={(e) => setFiatDetailsForm({ ...fiatDetailsForm, currency: e.target.value })}
                                    >
                                        <option value="">Select Currency</option>
                                        {currencies.map((currency) => (
                                            <option key={currency.code} value={currency.code}>
                                                {currency.name} ({currency.code})
                                                {currency.network && ` - ${currency.network}`}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Label</FormLabel>
                                    <Input
                                        placeholder="My Bank Account"
                                        value={fiatDetailsForm.label}
                                        onChange={(e) => setFiatDetailsForm({ ...fiatDetailsForm, label: e.target.value })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Beneficiary Name</FormLabel>
                                    <Input
                                        placeholder="Full Name"
                                        value={fiatDetailsForm.beneficiaryName}
                                        onChange={(e) => setFiatDetailsForm({ ...fiatDetailsForm, beneficiaryName: e.target.value })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Bank Name</FormLabel>
                                    <Input
                                        placeholder="Bank Name"
                                        value={fiatDetailsForm.bankDetails.bankName}
                                        onChange={(e) => setFiatDetailsForm({
                                            ...fiatDetailsForm,
                                            bankDetails: { ...fiatDetailsForm.bankDetails, bankName: e.target.value }
                                        })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Account Number</FormLabel>
                                    <Input
                                        placeholder="Account Number"
                                        value={fiatDetailsForm.bankDetails.accountNumber}
                                        onChange={(e) => setFiatDetailsForm({
                                            ...fiatDetailsForm,
                                            bankDetails: { ...fiatDetailsForm.bankDetails, accountNumber: e.target.value }
                                        })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>SWIFT Code (Optional)</FormLabel>
                                    <Input
                                        placeholder="SWIFT Code"
                                        value={fiatDetailsForm.bankDetails.swiftCode}
                                        onChange={(e) => setFiatDetailsForm({
                                            ...fiatDetailsForm,
                                            bankDetails: { ...fiatDetailsForm.bankDetails, swiftCode: e.target.value }
                                        })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>IBAN (Optional)</FormLabel>
                                    <Input
                                        placeholder="IBAN"
                                        value={fiatDetailsForm.bankDetails.iban}
                                        onChange={(e) => setFiatDetailsForm({
                                            ...fiatDetailsForm,
                                            bankDetails: { ...fiatDetailsForm.bankDetails, iban: e.target.value }
                                        })}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Country</FormLabel>
                                    <Input
                                        placeholder="Country"
                                        value={fiatDetailsForm.country}
                                        onChange={(e) => setFiatDetailsForm({ ...fiatDetailsForm, country: e.target.value })}
                                    />
                                </FormControl>

                                <Button colorScheme={colorSchemes.primary} onClick={handleAddFiatDetails} w="full">
                                    Add Bank Details
                                </Button>
                            </VStack>
                        </ModalBody>
                    </ModalContent>
                </Modal>

                {/* Create Balances Modal */}
                <Modal isOpen={createBalancesModal.isOpen} onClose={createBalancesModal.onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Create Balances</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={sectionSpacing}>
                            <VStack spacing={itemSpacing}>
                                <Text color={"text.muted"}>
                                    Select currencies for which you want to create balance accounts.
                                </Text>

                                <FormControl>
                                    <FormLabel>Available Currencies</FormLabel>
                                    <VStack align="start" spacing={smallSpacing} maxH="300px" overflowY="auto">
                                        {currencies.map((currency) => {
                                            const isSelected = createBalancesForm.selectedCurrencies.includes(currency.code || '');
                                            const hasBalance = balances.some(b => b.currency === currency.code);

                                            return (
                                                <HStack key={currency.code} w="full" justify="space-between">
                                                    <HStack>
                                                        <Text>{currency.name} ({currency.code})</Text>
                                                        {hasBalance && <Badge colorScheme={colorSchemes.success} size="sm">Already exists</Badge>}
                                                    </HStack>
                                                    <Button
                                                        size="sm"
                                                        colorScheme={isSelected ? colorSchemes.primary : colorSchemes.neutral}
                                                        variant={isSelected ? "solid" : "outline"}
                                                        disabled={hasBalance}
                                                        onClick={() => {
                                                            if (isSelected) {
                                                                setCreateBalancesForm({
                                                                    selectedCurrencies: createBalancesForm.selectedCurrencies.filter(
                                                                        c => c !== currency.code
                                                                    )
                                                                });
                                                            } else {
                                                                setCreateBalancesForm({
                                                                    selectedCurrencies: [...createBalancesForm.selectedCurrencies, currency.code || '']
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        {isSelected ? 'Remove' : hasBalance ? 'Exists' : 'Add'}
                                                    </Button>
                                                </HStack>
                                            );
                                        })}
                                    </VStack>
                                </FormControl>

                                {createBalancesForm.selectedCurrencies.length > 0 && (
                                    <Box p={3} bg="blue.50" borderRadius="md" w="full">
                                        <Text fontSize={smallFontSize} fontWeight="semibold" mb={1}>Selected currencies:</Text>
                                        <Text fontSize={smallFontSize} color={"text.primary"}>
                                            {createBalancesForm.selectedCurrencies.join(', ')}
                                        </Text>
                                    </Box>
                                )}

                                <Button
                                    colorScheme={colorSchemes.primary}
                                    onClick={handleCreateBalances}
                                    w="full"
                                    isDisabled={createBalancesForm.selectedCurrencies.length === 0}
                                >
                                    Create Balances
                                </Button>
                            </VStack>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            </VStack>
        </Container>
    );
};